import { useMemo, useRef, useState } from "react"
import { Send, ImagePlus, X } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import {
  COMMUNITY_CHANNELS,
  COMMUNITY_POST_MAX_IMAGES,
  uploadCommunityImage,
} from "../../community/community-service"

function extractTags(text) {
  const tagRe = /#(\w+)/g
  const tags = new Set()
  let m
  while ((m = tagRe.exec(text)) !== null) tags.add(m[1].toLowerCase())
  return [...tags]
}

export default function CommunityPostComposer({
  channel,
  onChannelChange,
  onSubmit,
  posting,
}) {
  const { currentUser, userProfile } = useAuth()

  const [message, setMessage] = useState("")
  const [imageFiles, setImageFiles] = useState([])
  const [imagePreviewUrls, setImagePreviewUrls] = useState([])
  const fileInputRef = useRef(null)

  const canSubmit = useMemo(() => {
    const trimmed = message.trim()
    return (trimmed.length > 0 || imageFiles.length > 0) && !posting && !!currentUser?.uid
  }, [message, imageFiles.length, posting, currentUser?.uid])

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index))
    setImagePreviewUrls((prev) => {
      const next = prev.filter((_, i) => i !== index)
      prev[index] && URL.revokeObjectURL(prev[index])
      return next
    })
  }

  const onFileChange = (e) => {
    const files = Array.from(e.target.files ?? [])
    const toAdd = files.slice(0, COMMUNITY_POST_MAX_IMAGES - imageFiles.length)
    if (toAdd.length === 0) return

    const newUrls = toAdd.map((f) => URL.createObjectURL(f))
    setImageFiles((prev) => [...prev, ...toAdd].slice(0, COMMUNITY_POST_MAX_IMAGES))
    setImagePreviewUrls((prev) => [...prev, ...newUrls].slice(0, COMMUNITY_POST_MAX_IMAGES))
    e.target.value = ""
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) return

    const trimmed = message.trim()
    const tags = extractTags(trimmed)

    let imageUrls = []
    if (imageFiles.length > 0) {
      try {
        imageUrls = await Promise.all(
          imageFiles.map((file) => uploadCommunityImage(currentUser.uid, file))
        )
      } catch (err) {
        console.error("Community image upload failed:", err)
        return
      }
    }

    onSubmit({
      channel,
      message: trimmed,
      tags,
      images: imageUrls.length ? imageUrls : undefined,
      // verified/name/avatar are attached by the page (TradeMetric pattern)
      // we keep composer focused on parsing + uploads.
      verified: !!userProfile?.is_verified,
    })

    setMessage("")
    setImageFiles([])
    imagePreviewUrls.forEach((u) => URL.revokeObjectURL(u))
    setImagePreviewUrls([])
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      {imagePreviewUrls.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {imagePreviewUrls.map((url, i) => (
            <div key={i} className="relative">
              <img
                src={url}
                alt=""
                className="h-14 w-14 rounded object-cover border border-gray-800/80"
              />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-500/90 transition-colors"
                aria-label="Remove image"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={onFileChange}
        />

        <button
          type="button"
          className="h-9 w-9 rounded-md bg-gray-800/60 hover:bg-gray-800/80 border border-gray-700/60 flex items-center justify-center disabled:opacity-50"
          disabled={posting || imageFiles.length >= COMMUNITY_POST_MAX_IMAGES}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Add image"
        >
          <ImagePlus className="h-4 w-4 text-emerald-300" />
        </button>

        <select
          value={channel}
          onChange={(e) => onChannelChange(e.target.value)}
          className="h-9 shrink-0 rounded-md bg-gray-800/40 border border-gray-700/70 px-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
          disabled={posting}
        >
          {COMMUNITY_CHANNELS.map((ch) => (
            <option key={ch} value={ch}>
              {ch === "profitloss" ? "Profit & Loss" : "General"}
            </option>
          ))}
        </select>

        <textarea
          placeholder="Message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={1}
          className="min-h-[36px] flex-1 resize-none rounded-md bg-gray-800/30 border border-gray-700/60 px-3 py-2 text-sm text-white shadow-none focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
          disabled={posting}
        />

        <button
          type="submit"
          className="h-9 w-9 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50 transition-colors flex items-center justify-center"
          disabled={!canSubmit}
          aria-label={posting ? "Posting…" : "Post"}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </form>
  )
}

